import { HiLockOpen } from "react-icons/hi2";
import { useUserRole } from "../Transactions/hooks/useUserRole";
import {
  ConfirmModal,
  ManageRoleModal,
  MerchantUserModal,
} from "../RBAC/modals/RBACModals";
import FetchFeedback from "../ui/CustomFetchFeedback/CustomFetchFeedback";
import { useUserFilters } from "../RBAC/hooks/users/useUserFilters";
import { useUserForm } from "../RBAC/hooks/users/useUserForm";
import { useUserModal } from "../RBAC/hooks/users/useUserModal";
import { useUserStatusHandlers } from "../RBAC/hooks/users/useUserStatusHandlers";
import { useUserRoleManagement } from "../RBAC/hooks/users/useUserRoleManagement";
import { useUserHandlers } from "../RBAC/hooks/users/useUserHandlers";
import { useUnblockPinAction } from "../RBAC/hooks/users/useUnblockPinAction";
import UserFilters from "../RBAC/tabs/components/users/UserFilters";
import UserTableContent from "../RBAC/tabs/components/users/UserTableContent";

const UserManagementContainerContent = ({
  users,
  title = "",
  icon = null,
  showCreateButton = false,
  createButtonText = "Add User",
  subtitle = "Manage system users",
  useCreateUserMutation,
  emptyStateMessage = "No Users have been created yet.",
  emptyStateSubMessage = "Start by creating your first user to give them access in your system.",
  searchPlaceholder = "Search users...",
  loadingData,
  isRefetching,
}) => {
  const { isSuperAdmin } = useUserRole();

  const createUserMutation = useCreateUserMutation();
  const { mutate: createUser, isPending: isCreating } = createUserMutation || {
    mutate: null,
    isPending: false,
  };

  const filterHook = useUserFilters({ users });
  const formHook = useUserForm();
  const modalHook = useUserModal({
    prefillForm: formHook.prefillForm,
    resetForm: formHook.resetForm,
  });
  const statusHook = useUserStatusHandlers();
  const roleManagementHook = useUserRoleManagement({ isSuperAdmin });
  const unblockPinHook = useUnblockPinAction();
  const handlerHook = useUserHandlers({
    modalMode: modalHook.modalMode,
    selectedUser: modalHook.selectedUser,
    closeModal: modalHook.closeModal,
    createUser,
  });

  return (
    <div className="h-full flex flex-col gap-2">
      <UserFilters
        title={title}
        icon={icon}
        subtitle={subtitle}
        showCreateButton={showCreateButton}
        createButtonText={createButtonText}
        onAddUser={modalHook.openCreateModal}
        isCreating={isCreating}
        hasCreateFn={!!createUser}
        onSearchChange={filterHook.setSearchTerm}
        badgeCount={filterHook.filteredUsers.length}
        searchPlaceholder={searchPlaceholder}
      />

      <UserTableContent
        loadingData={loadingData}
        noUsers={filterHook.noUsers}
        noResults={filterHook.noResults}
        filteredUsers={filterHook.filteredUsers}
        onEdit={modalHook.openEditModal}
        onToggleStatus={statusHook.handleToggleStatus}
        loadingIds={statusHook.loadingIds}
        onManageRoles={roleManagementHook.handleManageRoles}
        onUnblockPin={unblockPinHook.openUnblockModal}
        isRefetching={isRefetching}
        isSuperAdmin={isSuperAdmin}
        emptyStateMessage={emptyStateMessage}
        emptyStateSubMessage={emptyStateSubMessage}
      />

      <MerchantUserModal
        opened={modalHook.modalOpen}
        onClose={modalHook.closeModal}
        control={formHook.control}
        isSubmitting={handlerHook.isUpdating || isCreating}
        mode={modalHook.modalMode}
        onSubmit={formHook.handleSubmit(handlerHook.onSubmit)}
      />

      <ManageRoleModal
        opened={roleManagementHook.manageRolesModalOpen}
        onClose={roleManagementHook.handleCloseMerchantRolesModal}
        onSubmit={roleManagementHook.handleSubmit(roleManagementHook.onSubmit)}
        userName={roleManagementHook.selectedUser?.firstname || ""}
        control={roleManagementHook.control}
        roles={roleManagementHook.roles || []}
        loadingRoles={roleManagementHook.isLoadingRoles}
        isrolesError={roleManagementHook.isrolesError}
        isSubmitting={roleManagementHook.isAssigningRoles}
        refetchRoles={roleManagementHook.handleRetryRoles}
      />

      <ConfirmModal
        opened={unblockPinHook.modalOpen}
        onConfirm={unblockPinHook.handleConfirmUnblock}
        onCancel={unblockPinHook.closeUnblockModal}
        isLoading={unblockPinHook.isPending}
        title="Unblock PIN"
        message={`Unblock ${unblockPinHook.selectedUser?.firstname || "this user"}'s PIN? Retry count: ${unblockPinHook.selectedUser?.retryCount ?? "—"}.`}
        icon={<HiLockOpen style={{ fontSize: 18 }} />}
        iconColor="success"
        confirmColor="success"
        confirmLabel="Unblock"
        cancelLabel="Cancel"
      />
    </div>
  );
};

const UserManagementContainer = ({
  title = "",
  icon = null,
  showCreateButton = false,
  createButtonText = "Add User",
  subtitle = "Manage system users",
  useUsersQuery,
  useCreateUserMutation,
  emptyStateMessage = "No Users have been created yet.",
  emptyStateSubMessage = "Start by creating your first user to give them access in your system.",
  searchPlaceholder = "Search users...",
}) => {
  const { data, isLoading, isFetching, isError, refetch } = useUsersQuery();
  const users = Array.isArray(data) ? data : [];
  const hasInvalidUsersData = !isLoading && data != null && !Array.isArray(data);

  if (isError || hasInvalidUsersData) {
    return (
      <FetchFeedback
        message="Failed to load users. Please try again."
        onRetry={refetch}
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <UserManagementContainerContent
        users={users}
        title={title}
        icon={icon}
        showCreateButton={showCreateButton}
        createButtonText={createButtonText}
        subtitle={subtitle}
        useCreateUserMutation={useCreateUserMutation}
        emptyStateMessage={emptyStateMessage}
        emptyStateSubMessage={emptyStateSubMessage}
        searchPlaceholder={searchPlaceholder}
        loadingData={isLoading}
        isRefetching={isFetching}
      />
    </div>
  );
};

export default UserManagementContainer;
